import { useEffect, useState } from "react";
import { fetchDashboard, fetchGlobalDashboard, updateCoordenadorSales } from "../lib/api";
import { useSnapshot } from "../contexts/SnapshotContext";
import { useAuth } from "../contexts/AuthContext";
import { formatEUR, formatNum, RANK_LABELS } from "../lib/format";
import { KPICard } from "../components/KPICard";
import { RankBadge } from "../components/RankBadge";
import {
  CurrencyEur,
  Users,
  TrendUp,
  Crown,
  ArrowUpRight,
  WarningCircle,
  PencilSimple,
  Check,
  X,
  Star,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const RANK_COLORS_HEX = {
  Consultor: "#D5CFC5",
  "Lider Junior": "#E3A569",
  "Lider Ejecutivo": "#C57B67",
  "Lider Master": "#8A9A86",
};

export default function Dashboard() {
  const { selected, isGlobal, selectedPais } = useSnapshot();
  const { isSuperadmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingCoord, setEditingCoord] = useState(false);
  const [editingRate, setEditingRate] = useState(false);
  const [coordValue, setCoordValue] = useState("");
  const [rateValue, setRateValue] = useState("");
  const [savingCoord, setSavingCoord] = useState(false);

  const load = () => {
    setLoading(true);
    const p = isGlobal ? fetchGlobalDashboard(selectedPais) : fetchDashboard(selected?.id);
    p.then((d) => { setData(d); setLoading(false); });
  };

  useEffect(() => {
    load();
  }, [selected?.id, isGlobal]); // eslint-disable-line react-hooks/exhaustive-deps

  const startEditCoord = () => {
    setCoordValue(String(data?.snapshot?.coordenador_personal_sales ?? 0));
    setEditingCoord(true);
  };

  const startEditRate = () => {
    const r = (data?.snapshot?.coordenador_commission_rate ?? 0.09) * 100;
    setRateValue(String(r));
    setEditingRate(true);
  };

  const saveCoord = async (payload) => {
    setSavingCoord(true);
    try {
      await updateCoordenadorSales(data.snapshot.id, payload);
      toast.success("Datos actualizados");
      setEditingCoord(false);
      setEditingRate(false);
      await load();
    } catch (e) {
      toast.error("No se pudieron guardar los cambios");
    } finally {
      setSavingCoord(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-10">
        <div className="animate-pulse text-[#736F68]">Cargando datos...</div>
      </div>
    );
  }

  if (data?.empty) {
    return (
      <div className="p-6 md:p-10 max-w-3xl">
        <h1 className="text-3xl md:text-4xl font-semibold font-display text-[#2D2A26]">
          Bienvenida a tu panel Hesttya
        </h1>
        <p className="mt-3 text-[#736F68]">
          Aún no hay datos cargados. Sube tu primer archivo CSV para empezar a hacer
          seguimiento de las ventas y comisiones de tu equipo.
        </p>
        {!isSuperadmin && (
          <Link
            to="/subir"
            className="mt-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#C57B67] hover:bg-[#B06A57] text-white font-medium transition-colors"
            data-testid="dashboard-empty-upload-btn"
          >
            Subir CSV
            <ArrowUpRight size={16} weight="bold" />
          </Link>
        )}
      </div>
    );
  }

  const k = data.kpis;
  const rankData = Object.entries(data.distribucion_rank_qualifying).map(([k, v]) => ({
    name: k,
    value: v,
  }));
  const topPersonal = data.top_ventas_personales || [];
  const topEquipo = data.top_ventas_equipo || [];

  return (
    <div className="p-6 md:p-10 space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-xs tracking-widest uppercase text-[#736F68] font-display font-semibold">
            Periodo {data.snapshot.period}
          </p>
          <h1 className="mt-1 text-3xl md:text-4xl font-semibold font-display text-[#2D2A26] tracking-tight">
            {isSuperadmin
              ? (data.snapshot.coordenador || "Equipo")
              : `Hola, ${data.snapshot.coordenador?.split(" ")[0] || "coordinadora"}`}
          </h1>
          <p className="mt-1 text-[#736F68]">
            {isSuperadmin ? "Vista general · " : "Resumen de tu equipo · "}
            {k.total_consultores} consultoras
            {data.snapshot.uploaded_at && (
              <> · actualizado el{" "}
                {new Date(data.snapshot.uploaded_at).toLocaleDateString("es-ES", {
                  day: "2-digit", month: "short", year: "numeric", timeZone: "Europe/Madrid",
                })}
              </>
            )}
          </p>
        </div>
        {!isSuperadmin && (
          <Link
            to="/subir"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#E5E0D8] hover:bg-[#F2EFEA] text-sm text-[#2D2A26] transition-colors"
            data-testid="dashboard-upload-link"
          >
            Actualizar datos
            <ArrowUpRight size={14} weight="bold" />
          </Link>
        )}
      </header>

      {/* Coordinator's own personal sales + commission — hidden for superadmin and global view */}
      {!isSuperadmin && !isGlobal && <section
        className="bg-white border border-[#E5E0D8] rounded-xl p-6 grid grid-cols-1 md:grid-cols-3 gap-6 divide-y md:divide-y-0 md:divide-x divide-[#E5E0D8]"
        data-testid="coord-sales-card"
      >
        {/* Block 1: personal sales */}
        <div className="md:pr-6 pb-4 md:pb-0">
          <p className="text-[11px] font-semibold tracking-widest uppercase text-[#736F68] font-display">
            Tus ventas personales
          </p>
          <p className="mt-1 text-xs text-[#736F68]">Suman al volumen del equipo.</p>
          <div className="mt-3">
            {editingCoord ? (
              <div className="flex items-center gap-2">
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    value={coordValue}
                    onChange={(e) => setCoordValue(e.target.value)}
                    className="w-40 pl-3 pr-7 py-1.5 rounded-lg border border-[#C57B67] bg-white text-base font-mono focus:outline-none focus:ring-2 focus:ring-[#C57B67]/30"
                    autoFocus
                    data-testid="coord-sales-input"
                  />
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#736F68] text-sm">€</span>
                </div>
                <button
                  onClick={() => saveCoord({ coordenador_personal_sales: Number(coordValue) || 0 })}
                  disabled={savingCoord}
                  className="p-1.5 rounded-lg bg-[#C57B67] hover:bg-[#B06A57] text-white"
                  data-testid="coord-sales-save"
                >
                  <Check size={14} weight="bold" />
                </button>
                <button
                  onClick={() => setEditingCoord(false)}
                  className="p-1.5 rounded-lg border border-[#E5E0D8] text-[#736F68] hover:bg-[#F2EFEA]"
                >
                  <X size={14} weight="bold" />
                </button>
              </div>
            ) : (
              <button
                onClick={startEditCoord}
                className="inline-flex items-center gap-2 group"
                data-testid="coord-sales-edit"
              >
                <span className="text-2xl font-semibold font-display text-[#2D2A26]">
                  {formatEUR(data.snapshot.coordenador_personal_sales || 0)}
                </span>
                <span className="p-1.5 rounded-lg border border-[#E5E0D8] group-hover:bg-[#F2EFEA] text-[#736F68] group-hover:text-[#C57B67] transition-colors">
                  <PencilSimple size={12} weight="bold" />
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Block 2: commission rate */}
        <div className="md:px-6 pt-4 md:pt-0 pb-4 md:pb-0">
          <p className="text-[11px] font-semibold tracking-widest uppercase text-[#736F68] font-display">
            Tu % como coordinadora
          </p>
          <p className="mt-1 text-xs text-[#736F68]">
            Sobre el volumen comisionable ({formatEUR(data.snapshot.net_comisionable || 0)}).
          </p>
          <div className="mt-3">
            {editingRate ? (
              <div className="flex items-center gap-2">
                <div className="relative">
                  <input
                    type="number"
                    step="0.1"
                    value={rateValue}
                    onChange={(e) => setRateValue(e.target.value)}
                    className="w-24 pl-3 pr-7 py-1.5 rounded-lg border border-[#C57B67] bg-white text-base font-mono focus:outline-none focus:ring-2 focus:ring-[#C57B67]/30"
                    autoFocus
                    data-testid="coord-rate-input"
                  />
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#736F68] text-sm">%</span>
                </div>
                <button
                  onClick={() => saveCoord({ coordenador_commission_rate: Number(rateValue) || 0 })}
                  disabled={savingCoord}
                  className="p-1.5 rounded-lg bg-[#C57B67] hover:bg-[#B06A57] text-white"
                  data-testid="coord-rate-save"
                >
                  <Check size={14} weight="bold" />
                </button>
                <button
                  onClick={() => setEditingRate(false)}
                  className="p-1.5 rounded-lg border border-[#E5E0D8] text-[#736F68] hover:bg-[#F2EFEA]"
                >
                  <X size={14} weight="bold" />
                </button>
              </div>
            ) : (
              <button
                onClick={startEditRate}
                className="inline-flex items-center gap-2 group"
                data-testid="coord-rate-edit"
              >
                <span className="text-2xl font-semibold font-display text-[#2D2A26]">
                  {((data.snapshot.coordenador_commission_rate || 0) * 100).toFixed(1)}%
                </span>
                <span className="p-1.5 rounded-lg border border-[#E5E0D8] group-hover:bg-[#F2EFEA] text-[#736F68] group-hover:text-[#C57B67] transition-colors">
                  <PencilSimple size={12} weight="bold" />
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Block 3: total commission */}
        <div className="md:pl-6 pt-4 md:pt-0">
          <p className="text-[11px] font-semibold tracking-widest uppercase text-[#736F68] font-display">
            Tu comisión estimada
          </p>
          <p className="mt-1 text-xs text-[#736F68]">
            Equipo + 25% sobre tus ventas.
          </p>
          <div className="mt-3">
            <span
              className="text-2xl font-semibold font-display text-[#8A9A86]"
              data-testid="coord-total-commission"
            >
              {formatEUR(data.snapshot.coordenador_total_commission || 0)}
            </span>
            <p className="text-[11px] text-[#736F68] mt-1 font-mono">
              {formatEUR(data.snapshot.coordenador_team_commission || 0)} equipo +{" "}
              {formatEUR(data.snapshot.coordenador_direct_margin || 0)} directo
            </p>
          </div>
        </div>
      </section>}

      {/* KPIs */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <KPICard
          label="Ventas equipo"
          value={formatEUR(k.total_ventas_equipo)}
          sublabel={`Volumen total mensual`}
          icon={CurrencyEur}
          accent="primary"
          testid="kpi-ventas-equipo"
        />
        <KPICard
          label="Comisiones líderes"
          value={formatEUR(k.total_comisiones)}
          sublabel={
            k.total_bonos_ascenso > 0
              ? `Bonos ascenso: ${formatEUR(k.total_bonos_ascenso)} incluidos`
              : `Comisiones de equipo del mes`
          }
          icon={TrendUp}
          accent="success"
          testid="kpi-comisiones"
        />
        <KPICard
          label="Consultoras activas"
          value={`${k.consultores_activos} / ${k.total_consultores}`}
          sublabel={`${k.consultores_inactivos} inactivas este mes`}
          icon={Users}
          accent="neutral"
          testid="kpi-activos"
        />
        <KPICard
          label="Promociones disponibles"
          value={formatNum(k.promociones_disponibles)}
          sublabel="Cumplen requisitos para subir"
          icon={Crown}
          accent="warning"
          testid="kpi-promociones"
        />
      </section>

      {/* Charts row */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Top sellers */}
        <div className="bg-white border border-[#E5E0D8] rounded-xl p-6 lg:col-span-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-[#736F68] font-display">
              Top ventas personales
            </h3>
            <Link
              to="/consultoras"
              className="text-xs text-[#C57B67] hover:underline"
              data-testid="dashboard-see-all-consultores"
            >
              Ver todas →
            </Link>
          </div>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topPersonal} layout="vertical" margin={{ left: 12, right: 12, top: 6, bottom: 6 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EFEAE1" horizontal={false} />
                <XAxis type="number" stroke="#736F68" fontSize={11} />
                <YAxis
                  type="category"
                  dataKey="nome"
                  stroke="#736F68"
                  fontSize={11}
                  width={140}
                  tickFormatter={(v) => (v?.length > 18 ? v.slice(0, 17) + "…" : v)}
                />
                <Tooltip
                  cursor={{ fill: "#F2EFEA" }}
                  contentStyle={{
                    background: "#fff",
                    border: "1px solid #E5E0D8",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  formatter={(v) => formatEUR(v)}
                />
                <Bar dataKey="valor" fill="#C57B67" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Rank distribution */}
        <div className="bg-white border border-[#E5E0D8] rounded-xl p-6">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-[#736F68] font-display">
            Estatus calificable
          </h3>
          <div className="mt-2 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={rankData.filter((d) => d.value > 0)}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={45}
                  outerRadius={75}
                  paddingAngle={2}
                >
                  {rankData.map((entry, idx) => (
                    <Cell key={idx} fill={RANK_COLORS_HEX[entry.name] || "#D5CFC5"} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: "#fff",
                    border: "1px solid #E5E0D8",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Bonos de ascenso del mes */}
      <section className="bg-white border border-[#E3A569]/40 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-[#736F68] font-display">
                Bonos de ascenso del mes
              </h3>
              <p className="text-xs text-[#736F68] mt-0.5">
                Primera vez en este rango — cobran bono en lugar de comisión
              </p>
            </div>
            <Star size={20} className="text-[#E3A569]" weight="duotone" />
          </div>
          {data.bonos_ascenso?.length > 0 ? (
          <ul className="divide-y divide-[#F2EFEA]">
            {data.bonos_ascenso.map((b) => (
              <li key={`${b.id}-${b.rank}-${b.coordenador ?? ""}`} className="py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#2D2A26] truncate">{b.nome}</p>
                  <p className="text-xs text-[#736F68] mt-0.5">
                    Asciende a {RANK_LABELS[b.rank] ?? b.rank}
                    {isGlobal && b.coordenador && (
                      <span className="ml-1 text-[#B5AFA8]">· {b.coordenador}</span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <RankBadge rank={b.rank} />
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#E3A569] text-white text-sm font-bold font-mono shadow-sm ring-2 ring-[#E3A569]/30">
                    +{formatEUR(b.bono)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
          ) : (
            <p className="text-sm text-[#736F68]">
              Ningún ascenso de primera vez este mes.
            </p>
          )}
      </section>

      {/* Promotions + Team team sales table */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white border border-[#E5E0D8] rounded-xl p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-[#736F68] font-display">
              Promociones disponibles
            </h3>
            <Crown size={18} className="text-[#E3A569]" weight="duotone" />
          </div>
          {data.promociones?.length ? (
            <ul className="mt-4 divide-y divide-[#F2EFEA]">
              {data.promociones.slice(0, 6).map((p) => (
                <li
                  key={p.id}
                  className="py-3 flex items-center justify-between gap-3"
                  data-testid="promo-item"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#2D2A26] truncate">{p.nome}</p>
                    <p className="text-xs text-[#736F68]">
                      Actualmente {p.actual} → califica para {p.nuevo}
                    </p>
                  </div>
                  <RankBadge rank={p.nuevo} />
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-6 text-sm text-[#736F68]">
              Ninguna consultora cumple actualmente los requisitos para subir de estatus.
            </p>
          )}
        </div>

        <div className="bg-white border border-[#E5E0D8] rounded-xl p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-[#736F68] font-display">
              Top líderes de equipo
            </h3>
            <TrendUp size={18} className="text-[#8A9A86]" weight="duotone" />
          </div>
          <ul className="mt-4 divide-y divide-[#F2EFEA]">
            {topEquipo.length === 0 && (
              <li className="py-4 text-sm text-[#736F68]">Sin datos.</li>
            )}
            {topEquipo.map((c) => (
              <li
                key={c.id}
                className="py-3 flex items-center justify-between"
                data-testid="top-equipo-item"
              >
                <div>
                  <p className="text-sm font-medium text-[#2D2A26]">{c.nome}</p>
                  <RankBadge rank={c.titulo} />
                </div>
                <p className="font-mono text-sm text-[#2D2A26] font-semibold">
                  {formatEUR(c.valor)}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* En riesgo banner */}
      {k.en_riesgo > 0 && (
        <Link
          to="/alertas"
          className="block bg-[#B85C5C]/8 border border-[#B85C5C]/30 rounded-xl p-5 hover:bg-[#B85C5C]/12 transition-colors"
          style={{ backgroundColor: "rgba(184, 92, 92, 0.06)" }}
          data-testid="dashboard-risk-banner"
        >
          <div className="flex items-start gap-3">
            <WarningCircle size={22} className="text-[#B85C5C] mt-0.5" weight="duotone" />
            <div>
              <p className="text-sm font-semibold text-[#2D2A26]">
                {k.en_riesgo} consultoras en riesgo
              </p>
              <p className="text-xs text-[#736F68] mt-0.5">
                Revisa los detalles y toma acción a tiempo →
              </p>
            </div>
          </div>
        </Link>
      )}
    </div>
  );
}